import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { MatChip } from "@angular/material/chips";
import { DebugModeService } from "../../../../../services/debug-mode.service";
import { ContentDataViewerComponent } from "../../../shared/content-data-viewer/content-data-viewer.component";
import {
  formatStatValue,
  getCaptureMetadata,
  getMetadataValue,
  getOriginStats,
  getRequestedParams,
  humanizeMapLabel,
  resolveBlockRef,
} from "../../../../../model/block.types";
import type { BlockStringMap, ContentBlockData, ContentBlockKind } from "../../../../../model/block.types";
import type { DataResult } from "../../../../../model/data.types";

/** One debug chip: label + already-stringified value. */
interface DebugChip {
  label: string;
  value: string;
  /**
   * Warning styling for the partial-data summary chip (S9 truncation prominence). When
   * set, `label` carries the COMPLETE display text (e.g. "Partial data: 1/3 pages") and
   * the template renders it without the generic label:value separator.
   */
  warn?: boolean;
}

/** Descriptor of one fixed metadata field rendered as a debug chip for a block kind. */
interface DebugChipDescriptor {
  /** Metadata field holding the value. */
  field: string;
  /** Fixed display label of the chip. */
  label: string;
}

/** The three geo kinds share the same fixed payload fields. */
const GEO_CHIP_DESCRIPTORS: readonly DebugChipDescriptor[] = [
  { field: "feature_count", label: "Features" },
  { field: "geometry_types", label: "Geometry types" },
  { field: "crs", label: "CRS" },
  { field: "zoom_hint", label: "Zoom hint" },
];

/** image and geotiff share the same raster payload fields. */
const RASTER_CHIP_DESCRIPTORS: readonly DebugChipDescriptor[] = [
  { field: "resolution_x", label: "Res. X" },
  { field: "resolution_y", label: "Res. Y" },
  { field: "bands", label: "Bands" },
  { field: "dtype", label: "Dtype" },
  { field: "min_value", label: "Min" },
  { field: "max_value", label: "Max" },
];

/**
 * Fixed-field chip descriptors per block kind (backend contract table, S5). Pure data on
 * purpose: a new kind or field is one table row, no per-kind conditionals. A descriptor
 * whose field is absent from metadata produces NO chip — that IS the never-render-empty
 * rule. Kinds without fixed fields (message, layer_spec, …) have no entry.
 */
const DEBUG_CHIP_DESCRIPTORS: Partial<Record<ContentBlockKind, readonly DebugChipDescriptor[]>> = {
  geometry: GEO_CHIP_DESCRIPTORS,
  geolocation: GEO_CHIP_DESCRIPTORS,
  place: GEO_CHIP_DESCRIPTORS,
  structured: [
    { field: "key_count", label: "Keys" },
    { field: "top_level_keys", label: "Top-level keys" },
    { field: "scalar_values", label: "Scalar values" },
  ],
  tabular: [
    { field: "row_count", label: "Rows" },
    { field: "columns", label: "Columns" },
  ],
  image: RASTER_CHIP_DESCRIPTORS,
  geotiff: RASTER_CHIP_DESCRIPTORS,
  blob: [
    { field: "page_count", label: "Pages" },
    { field: "title", label: "Title" },
    { field: "char_count", label: "Chars" },
  ],
};

/** Compact byte size for the ref.size_bytes chip: "820 B" / "12.3 KB" / "4.8 MB". */
function formatSizeBytes(sizeBytes: number | null): string | null {
  if (sizeBytes === null || !Number.isFinite(sizeBytes) || sizeBytes < 0) return null;
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Parses a string stat value as a finite non-negative number; null when it does not parse. */
function parseStatNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/**
 * Builds the prominent partial-data summary chip from `pagination_*` origin stats (S9:
 * "pagination_* only present when the response was TRUNCATED — surface it prominently").
 * The complete display text lives in `label` — the template renders warn chips without
 * the generic label:value separator. Count preference: `pagination_delivered_items` +
 * `pagination_total_pages` ("Partial data: X/Y pages"), else `pagination_delivered_pages`
 * + `pagination_total_pages`, else the raw `pagination_reason` ("Partial data (reason)"),
 * else a bare "Partial data". null when the stats carry no `pagination_*` key at all —
 * absent pagination never produces the chip (S9 rule 1).
 */
function partialDataChip(stats: BlockStringMap): DebugChip | null {
  if (!Object.keys(stats).some((key) => key.startsWith("pagination_"))) return null;
  const totalPages = parseStatNumber(stats["pagination_total_pages"] ?? "");
  const deliveredItems = parseStatNumber(stats["pagination_delivered_items"] ?? "");
  const deliveredPages = parseStatNumber(stats["pagination_delivered_pages"] ?? "");
  let label: string;
  if (totalPages !== null && deliveredItems !== null) {
    label = `Partial data: ${deliveredItems}/${totalPages} pages`;
  } else if (totalPages !== null && deliveredPages !== null) {
    label = `Partial data: ${deliveredPages}/${totalPages} pages`;
  } else if (stats["pagination_reason"]) {
    label = `Partial data (${stats["pagination_reason"]})`;
  } else {
    label = "Partial data";
  }
  return { label, value: "", warn: true };
}

/**
 * The S9 data.source traceability ids, in their fixed render order
 * (task → attempt → capability). Pure data: a new id is one table row.
 */
const SOURCE_ID_DESCRIPTORS: readonly DebugChipDescriptor[] = [
  { field: "task_id", label: "Task" },
  { field: "attempt_id", label: "Attempt" },
  { field: "capability_id", label: "Capability" },
];

/**
 * Defensive stringify for one source traceability id (S9 data.source bullet): string
 * values pass through as-is, numbers stringify, everything else (objects, arrays,
 * null/undefined, empty string) is skipped — null means "no usable value, render
 * nothing for this id".
 */
function sourceLineValue(value: unknown): string | null {
  if (typeof value === "string") return value === "" ? null : value;
  if (typeof value === "number") return String(value);
  return null;
}

/**
 * Debug-only metadata section shared by every content-block card kind (S1/S2/S4): one chips
 * row with the fixed per-kind payload fields from the descriptor table plus ref size/mime,
 * the requested_params object through the shared JSON viewer (labeled "Requested params"),
 * the open-ended origin_stats map as generic key:value chips in their own labeled
 * "Origin stats" row (labels humanized from the keys, *_ms values formatted client-side,
 * plus one warning-styled "Partial data" summary chip prepended when pagination_* keys
 * signal a truncated response), the open-ended capture_metadata map (imagery XMP
 * facts) as generic key:value chips in their own labeled "Capture info" row (labels
 * humanized from the keys, values as strings, so future keys appear without UI changes),
 * and the S9 data.source traceability ids (task_id/attempt_id/capability_id) as ONE plain
 * muted text line — never chips.
 * Renders nothing unless debug mode is on AND there is something to show; absent
 * fields/maps simply produce no chips/sections/lines.
 */
@Component({
  selector: "app-block-debug-meta",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatChip, ContentDataViewerComponent],
  template: `
    @if (visible()) {
      <div class="block-debug-meta">
        @if (chips().length > 0) {
          <div class="px-3 pb-2 d-flex flex-wrap gap-1">
            @for (chip of chips(); track $index) {
              <mat-chip>{{ chip.label }}: {{ chip.value }}</mat-chip>
            }
          </div>
        }
        @if (requestedParamsResult(); as params) {
          <div class="px-3 pb-2">
            <span class="block-debug-meta__label mat-font-label-sm text-body-secondary d-block mb-1">Requested params</span>
            <app-content-data-viewer [data]="params" />
          </div>
        }
        @if (statsChips().length > 0) {
          <div class="px-3 pb-2">
            <span class="block-debug-meta__label mat-font-label-sm text-body-secondary d-block mb-1">Origin stats</span>
            <div class="d-flex flex-wrap gap-1">
              @for (chip of statsChips(); track $index) {
                @if (chip.warn) {
                  <mat-chip color="warn" class="text-warning">{{ chip.label }}</mat-chip>
                } @else {
                  <mat-chip>{{ chip.label }}: {{ chip.value }}</mat-chip>
                }
              }
            </div>
          </div>
        }
        @if (captureChips().length > 0) {
          <div class="px-3 pb-2">
            <span class="block-debug-meta__label mat-font-label-sm text-body-secondary d-block mb-1">Capture info</span>
            <div class="d-flex flex-wrap gap-1">
              @for (chip of captureChips(); track $index) {
                <mat-chip>{{ chip.label }}: {{ chip.value }}</mat-chip>
              }
            </div>
          </div>
        }
        @if (sourceLine(); as source) {
          <div class="px-3 pb-2">
            <span class="block-debug-meta__source mat-font-label-sm text-body-secondary d-block">{{ source }}</span>
          </div>
        }
      </div>
    }
  `,
})
export class BlockDebugMetaComponent {
  kind = input.required<ContentBlockKind>();

  data = input.required<ContentBlockData>();

  protected readonly debugMode = inject(DebugModeService);

  /**
   * First-row debug chips: fixed per-kind fields from the descriptor table, then ref
   * size/mime. requested_params and origin_stats are NOT part of this row — params render
   * through the JSON viewer and stats through their own labeled statsChips row.
   */
  protected readonly chips = computed((): DebugChip[] => {
    const metadata = this.data().metadata;
    const chips: DebugChip[] = [];
    for (const descriptor of DEBUG_CHIP_DESCRIPTORS[this.kind()] ?? []) {
      const value = getMetadataValue(metadata, descriptor.field);
      if (value !== null) chips.push({ label: descriptor.label, value });
    }
    const ref = resolveBlockRef(this.data().ref);
    const size = formatSizeBytes(ref?.size_bytes ?? null);
    if (size !== null) chips.push({ label: "Size", value: size });
    if (ref?.mime_type) chips.push({ label: "MIME", value: ref.mime_type });
    return chips;
  });

  /** requested_params as a JSON DataResult for the shared viewer; null when absent/empty. */
  protected readonly requestedParamsResult = computed((): DataResult | null => {
    const params = getRequestedParams(this.data().metadata);
    return params ? { type: "json", json: params } : null;
  });

  /**
   * origin_stats entries as generic key:value chips for the labeled "Origin stats" row
   * (label = humanized key per S9 rule 3, value client-formatted via formatStatValue so
   * *_ms latencies read as ms/seconds per S9 rule 2), so unknown future producer stats
   * appear without UI changes. When the map carries any pagination_* key (S9: the
   * response was truncated), one warning-styled "Partial data" summary chip is PREPENDED
   * — the individual pagination chips remain in the row (open set, additive summary).
   * Empty when the map is absent/empty (never render empty).
   */
  protected readonly statsChips = computed((): DebugChip[] => {
    const stats = getOriginStats(this.data().metadata);
    if (!stats) return [];
    const summary = partialDataChip(stats);
    const chips: DebugChip[] = summary ? [summary] : [];
    for (const [key, value] of Object.entries(stats)) {
      chips.push({ label: humanizeMapLabel(key), value: formatStatValue(key, value) });
    }
    return chips;
  });

  /**
   * capture_metadata entries as generic key:value chips for the labeled "Capture info" row
   * (label = humanized key, value as string), so unknown future capture facts appear without
   * UI changes; empty when the map is absent/empty (never render empty).
   */
  protected readonly captureChips = computed((): DebugChip[] => {
    const capture = getCaptureMetadata(this.data().metadata);
    return capture ? Object.entries(capture).map(([key, value]) => ({ label: humanizeMapLabel(key), value })) : [];
  });

  /**
   * The S9 data.source traceability ids as ONE plain muted text line ("Task x · Attempt 2 ·
   * Capability y"), fixed task → attempt → capability order, carrying only the ids that
   * hold a usable value; null when none does (never render an empty line). Foreign source
   * keys (evidence_id, tool_name) intentionally never render here.
   */
  protected readonly sourceLine = computed((): string | null => {
    const source = this.data().source;
    if (!source) return null;
    const parts: string[] = [];
    for (const descriptor of SOURCE_ID_DESCRIPTORS) {
      const value = sourceLineValue(source[descriptor.field]);
      if (value !== null) parts.push(`${descriptor.label} ${value}`);
    }
    return parts.length > 0 ? parts.join(" · ") : null;
  });

  /** S1: renders only in debug mode AND only when there is something to show. */
  protected readonly visible = computed(
    () =>
      this.debugMode.isDebugMode() &&
      (this.chips().length > 0 ||
        this.statsChips().length > 0 ||
        this.captureChips().length > 0 ||
        this.sourceLine() !== null ||
        this.requestedParamsResult() !== null),
  );
}
